/**
 * 火种技能树面板
 *
 * 设计（GDD 3.2）：**技能效果本身就是知识的应用**。
 * 所以每个节点卡片上同时给出「学到什么」（卡片标题）与「变强什么」（效果摘要），
 * 并且节点被锁住时，明确写出怎么解开它 —— 而不是简单地变灰。
 */

import type { LevelConfig, MatchSetup, OptionView, SimState, SkillEffect } from "@chuandeng/shared";
import { cardById } from "../content/cards.js";
import { esc } from "./dom.js";
import { type ModalHandle, openModal } from "./modal.js";
import { reasonText } from "./reasons.js";

const EFFECT_TEXT: Record<SkillEffect["kind"], (value: number) => string> = {
  spreadSpeed: (v) => `传播速度 +${v}%`,
  spreadRadius: (v) => `传播波及 +${v} 个相邻区域`,
  energyRegen: (v) => `每回合能量 +${v}`,
  maxEnergy: (v) => `能量上限 +${v}`,
  mistResist: (v) => `迷雾抗性 +${v}/回合`,
  revealRoute: (v) => `显现隐藏航路 +${v} 条`,
};

export function describeEffects(effects: readonly SkillEffect[]): string {
  return effects.map((effect) => EFFECT_TEXT[effect.kind](effect.value)).join("，");
}

export interface SkillTreeOptions {
  /** 每次升级后重新取一次最新局势（单一状态源在 session 里） */
  getView(): { setup: MatchSetup; state: SimState; options: OptionView; level: LevelConfig };
  onUpgrade(nodeId: string): boolean;
  /** 首次解锁时的知识图鉴 */
  onOpenCodex(cardId: string): void;
  onClose?(): void;
}

export function openSkillTree(options: SkillTreeOptions): ModalHandle {
  const handle = openModal({
    eyebrow: "火种技能树",
    title: "把知识变成火种的力量",
    bodyHtml: "<p>加载中…</p>",
    actions: [
      { label: "打开知识图鉴", value: "codex", kind: "ghost" },
      { label: "继续游戏", value: "close" },
    ],
    onAction: (value) => {
      if (value === "close") handle.close();
      if (value === "codex") {
        const { state } = options.getView();
        const first = state.unlockedCardIds[0];
        if (first) options.onOpenCodex(first);
      }
    },
    onClose: options.onClose,
  });

  const render = (): void => {
    const { setup, state, options: view } = options.getView();
    const unlocked = new Set(state.unlockedNodeIds);
    const nodeCards = view.nodes
      .map((option) => {
        const node = option.node;
        const isUnlocked = unlocked.has(node.id);
        const card = cardById(node.cardId ?? "");
        const classes = [
          "tree-node",
          isUnlocked ? "is-unlocked" : "",
          !isUnlocked && !option.unlockable ? "is-locked" : "",
        ]
          .filter(Boolean)
          .join(" ");
        const statusLine = isUnlocked
          ? `<span class="badge badge--ok">✓ 已学会</span>`
          : option.unlockable
            ? `<span class="badge badge--draft">可升级</span>`
            : `<span class="tree-node-reason">🔒 ${esc(reasonText(option.reason))}</span>`;
        return `
          <button class="${classes}" type="button" data-node="${esc(node.id)}"
                  ${!isUnlocked && option.unlockable ? "" : "disabled"}>
            <span class="tree-node-name">${esc(node.name)}</span>
            <span class="tree-node-cost">${isUnlocked ? "已解锁" : `需要 ${node.cost} 智慧值`}</span>
            <span class="tree-node-tier">效果：${esc(describeEffects(node.effects))}</span>
            <span class="tree-node-tier">知识：${esc(card?.title ?? "（暂无对应卡片）")}</span>
            ${statusLine}
          </button>`;
      })
      .join("");

    handle.setBody(
      `
      <div class="codex-meta">
        <span class="badge badge--info">智慧值 ${Math.round(state.wisdom)}</span>
        <span class="badge badge--info">已解锁 ${state.unlockedNodeIds.length} / ${setup.skillTree.length}</span>
        <span class="badge badge--info">迷雾抗性 ${view.effects.mistResist}/回合</span>
        <span class="badge badge--info">传播速度 ×${view.effects.spreadSpeedMul.toFixed(2)}</span>
      </div>
      <p style="font-size:.86rem;color:var(--ink-soft)">
        升级会消耗智慧值（每答对一道题 +15，传播一次 +3）。每个节点背后都是一张真实的知识卡。
      </p>
      <div class="tree">${nodeCards}</div>`,
      { title: "把知识变成火种的力量" },
    );

    handle.element.querySelector(".tree")?.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest<HTMLElement>("[data-node]");
      if (!button || button.hasAttribute("disabled")) return;
      const nodeId = button.dataset["node"];
      if (!nodeId) return;

      const node = setup.skillTree.find((n) => n.id === nodeId);
      if (options.onUpgrade(nodeId) && node?.cardId) {
        // 首次解锁 → 立刻弹出知识图鉴（教学与玩法统一的关键时刻）
        options.onOpenCodex(node.cardId);
      }
      render();
    });
  };

  render();
  return handle;
}
